// ─────────────────────────────────────────────────────────────────
// Glass-i — Accounting period guard (Stage 6B.9F — Hardening).
//
// Pure-JS helpers (no JSX, no React, no axios) so node:test can
// import them directly and the JSX components re-export the behaviour.
// Used by the Journal / Payment / Receipt / Expense / Contra voucher
// modals to give the operator early feedback BEFORE submitting a
// posting request that the backend will reject anyway.
//
// IMPORTANT
//   Backend is the source of truth — the engine re-checks the
//   accounting_periods table inside the posting transaction and
//   rejects with VOUCHER_PERIOD_LOCKED / VOUCHER_PERIOD_CLOSED /
//   VOUCHER_PERIOD_MISSING. This helper just pre-empts the round-trip
//   so the operator gets a useful message before clicking Post.
//
// API
// ─────────────────────────────────────────────────────────────────
//   classifyPeriodForDate(periods, voucherDate)
//     → { ok: true,  period }   — voucher date falls in an OPEN period
//       { ok: false, status: 'missing' | 'locked' | 'closed',
//         period?, message }
//
//   periodGuardError(classification)
//     → null when ok=true, otherwise a friendly { code, message }
//       envelope so the modal can render a single Alert.
// ─────────────────────────────────────────────────────────────────

// Normalize a date input (string or Date) to the YYYY-MM-DD shape the
// API uses for period_start / period_end / voucher_date. Returns null
// when the input cannot be parsed.
export function toIsoDate(value) {
  if (!value) return null;
  if (typeof value === 'string') {
    // Accept "YYYY-MM-DD..." prefix verbatim — the API serializes
    // dates either as "YYYY-MM-DD" or "YYYY-MM-DDTHH:MM:SS.SSSZ".
    const m = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString().slice(0, 10);
  }
  // Plain Date OR dayjs (which has .format) — try .format first.
  if (typeof value === 'object') {
    if (typeof value.format === 'function') return value.format('YYYY-MM-DD');
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value.toISOString().slice(0, 10);
    }
  }
  return null;
}

// `periods` shape — the same envelope the /periods/list endpoint
// returns: an array of rows with period_start, period_end, status,
// period_name, period_type ('month' | 'quarter' | 'year').
//
// We classify by the FIRST monthly period that covers the voucher
// date — mirrors the backend's `assertPostablePeriodOnClient` which
// only considers period_type='month'. Quarterly / yearly periods are
// reporting aggregates and never block posting.
export function classifyPeriodForDate(periods, voucherDate) {
  const date = toIsoDate(voucherDate);
  if (!date) {
    return {
      ok: false, status: 'missing', period: null,
      message: 'Please pick a valid voucher date.'
    };
  }
  const rows = Array.isArray(periods) ? periods : [];
  const monthly = rows.filter((p) =>
    (p.periodType || p.period_type || 'month') === 'month');
  const match = monthly.find((p) => {
    const start = toIsoDate(p.periodStart || p.period_start);
    const end   = toIsoDate(p.periodEnd   || p.period_end);
    return start && end && date >= start && date <= end;
  });
  if (!match) {
    return {
      ok: false, status: 'missing', period: null,
      message: `No accounting period is configured for ${date}. Generate FY periods in Accounts Setup before posting.`
    };
  }
  const status = match.status || 'open';
  const name   = match.periodName || match.period_name || 'this period';
  if (status === 'open') {
    return { ok: true, status: 'open', period: match };
  }
  if (status === 'closed') {
    return {
      ok: false, status: 'closed', period: match,
      message: `Accounting period "${name}" is closed. You cannot post vouchers in a closed period.`
    };
  }
  // locked or any other non-open state.
  return {
    ok: false, status: 'locked', period: match,
    message: `Accounting period "${name}" is locked. You cannot post vouchers in a locked period.`
  };
}

// Convenience — produces the friendly envelope the modal renders.
export function periodGuardError(classification) {
  if (!classification || classification.ok) return null;
  let code = 'VOUCHER_PERIOD_LOCKED';
  if (classification.status === 'closed')  code = 'VOUCHER_PERIOD_CLOSED';
  if (classification.status === 'missing') code = 'VOUCHER_PERIOD_MISSING';
  return { code, message: classification.message };
}

// Convenience — boolean predicate, useful for disabling the Post
// button without rendering an Alert (the modal still has to decide
// where to show the message).
export function isPostablePeriod(classification) {
  return !!(classification && classification.ok === true);
}

// Build a `loader` for the modal: returns the full periods list for
// the current tenant via the standard `/periods/list` endpoint.
// Caller supplies the `accountsApi` to avoid an import cycle (the
// helpers module lives next to accountsApi and dragging the axios
// client into a pure helper module would defeat the test isolation).
export async function loadPeriodsForGuard(accountsApi, { pageSize = 200 } = {}) {
  if (!accountsApi || typeof accountsApi.listPeriods !== 'function') {
    return { rows: [] };
  }
  // Request the maximum page; the backend caps at ~500 rows per page.
  // Filter to monthly periods only (the only kind that blocks posting).
  try {
    const out = await accountsApi.listPeriods({
      page: 1, pageSize,
      filters: [{ field: 'period_type', op: 'eq', value: 'month' }]
    });
    return out || { rows: [] };
  } catch (_e) {
    return { rows: [] };
  }
}
